import { useState, useEffect, useMemo } from 'react';
import { DateRangeFilter } from '@/components/DateRangeFilter';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ArrowLeft, RefreshCw, ExternalLink, Landmark, TrendingUp, TrendingDown, ArrowUpDown, ArrowUp, ArrowDown, X, Search, Filter, Save, History, Trash2, Clock, BarChart3, Download, Trophy, Globe } from 'lucide-react';
import { PoliticianScorecard } from '@/components/PoliticianScorecard';
import { TickerScorecard } from '@/components/TickerScorecard';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend, AreaChart, Area, Line, ComposedChart } from 'recharts';
import * as XLSX from 'xlsx';

interface CapitolTrade {
  politician: string;
  party: string;
  chamber: string;
  state: string;
  issuer: string;
  ticker: string;
  tickerSource?: 'scraped' | 'fmp' | 'abbreviated';
  published: string;
  traded: string;
  filedAfter: string;
  owner: string;
  type: string;
  size: string;
  price: string;
  tradeUrl: string;
  _source?: 'capitoltrades' | 'quiver' | 'both';
}

type SortField = 'politician' | 'party' | 'type' | 'traded' | 'ticker' | 'price';
type SortDir = 'asc' | 'desc' | null;

type DataSource = 'capitoltrades' | 'quiver' | 'combined';

const CapitolTrades = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [capitolTrades, setCapitolTrades] = useState<CapitolTrade[]>([]);
  const [quiverTrades, setQuiverTrades] = useState<CapitolTrade[]>([]);
  const [dataSource, setDataSource] = useState<DataSource>('capitoltrades');
  const [loading, setLoading] = useState(false);
  const [loadingQuiver, setLoadingQuiver] = useState(false);
  const [fetchedAt, setFetchedAt] = useState<string | null>(null);
  const [quiverFetchedAt, setQuiverFetchedAt] = useState<string | null>(null);
  const [snapshots, setSnapshots] = useState<Array<{ id: string; trades_count: number; fetched_at: string; created_at: string }>>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [chartMode, setChartMode] = useState<'politician' | 'party'>('politician');
  const [timelineGranularity, setTimelineGranularity] = useState<'week' | 'month'>('week');
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 25;
  const [viewingSnapshot, setViewingSnapshot] = useState<string | null>(null);
  const [selectedPolitician, setSelectedPolitician] = useState<string | null>(null);
  const [selectedTicker, setSelectedTicker] = useState<{ ticker: string; issuer: string } | null>(null);
  const [leaderboardSort, setLeaderboardSort] = useState<'total' | 'net' | 'buys'>('total');

  // Merge and deduplicate trades from both sources
  const combinedTrades = useMemo((): CapitolTrade[] => {
    if (capitolTrades.length === 0 && quiverTrades.length === 0) return [];

    const all: CapitolTrade[] = [
      ...capitolTrades.map(t => ({ ...t, _source: 'capitoltrades' as const })),
      ...quiverTrades.map(t => ({ ...t, _source: 'quiver' as const })),
    ];

    // Dedup: match on politician + ticker + type + traded date
    const seen = new Map<string, CapitolTrade>();

    all.forEach(t => {
      const key = [
        t.politician.toLowerCase().trim(),
        (t.ticker || '').toLowerCase().trim(),
        (t.type || '').toLowerCase().replace('purchase', 'buy').replace('sale', 'sell').trim(),
        (t.traded || '').trim(),
      ].join('|');

      if (seen.has(key)) {
        const existing = seen.get(key)!;
        if (existing._source !== 'capitoltrades' && t._source === 'capitoltrades') {
          seen.set(key, { ...t, _source: 'both' });
        } else if (existing._source !== 'both') {
          seen.set(key, { ...existing, _source: 'both' });
        }
      } else {
        seen.set(key, t);
      }
    });

    return Array.from(seen.values());
  }, [capitolTrades, quiverTrades]);

  // Active trades based on selected source
  const trades = dataSource === 'capitoltrades' ? capitolTrades : dataSource === 'quiver' ? quiverTrades : combinedTrades;
  const activeFetchedAt = dataSource === 'capitoltrades' ? fetchedAt : dataSource === 'quiver' ? quiverFetchedAt : fetchedAt || quiverFetchedAt;
  const isActiveLoading = dataSource === 'capitoltrades' ? loading : dataSource === 'quiver' ? loadingQuiver : loading || loadingQuiver;

  // Filters
  const [partyFilter, setPartyFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [chamberFilter, setChamberFilter] = useState<string>('all');
  const [sizeFilter, setSizeFilter] = useState<string>('all');
  const [issuerFilter, setIssuerFilter] = useState<string>('all');
  const [tickerSourceFilter, setTickerSourceFilter] = useState<string>('all');
  const [politicianSearch, setPoliticianSearch] = useState('');
  const [timeRange, setTimeRange] = useState<string>('all');
  const [dateFrom, setDateFrom] = useState<Date | undefined>(undefined);
  const [dateTo, setDateTo] = useState<Date | undefined>(undefined);

  // Sorting
  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>(null);

  const toggleSort = (field: SortField) => {
    if (sortField === field) {
      if (sortDir === 'asc') setSortDir('desc');
      else if (sortDir === 'desc') { setSortField(null); setSortDir(null); }
      else setSortDir('asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const parseTradedDate = (dateStr: string): Date | null => {
    if (!dateStr) return null;
    const d = new Date(dateStr);
    return isNaN(d.getTime()) ? null : d;
  };

  const getTimeRangeCutoff = (range: string): Date | null => {
    if (range === 'all') return null;
    const now = new Date();
    switch (range) {
      case '30d': return new Date(now.getTime() - 30 * 86400000);
      case '3m': return new Date(now.setMonth(now.getMonth() - 3));
      case '6m': return new Date(now.setMonth(now.getMonth() - 6));
      case '1y': return new Date(now.setFullYear(now.getFullYear() - 1));
      default: return null;
    }
  };

  const filteredAndSorted = useMemo(() => {
    let result = [...trades];

    if (partyFilter !== 'all') {
      result = result.filter(t => t.party.toLowerCase() === partyFilter.toLowerCase());
    }
    if (typeFilter !== 'all') {
      result = result.filter(t => {
        const tt = t.type.toLowerCase();
        if (typeFilter === 'buy') return tt === 'buy' || tt === 'purchase';
        if (typeFilter === 'sell') return tt === 'sell' || tt === 'sale';
        return tt === typeFilter;
      });
    }
    if (chamberFilter !== 'all') {
      result = result.filter(t => t.chamber.toLowerCase() === chamberFilter.toLowerCase());
    }
    if (sizeFilter !== 'all') {
      result = result.filter(t => t.size === sizeFilter);
    }
    if (issuerFilter !== 'all') {
      result = result.filter(t => t.issuer === issuerFilter);
    }
    if (tickerSourceFilter !== 'all') {
      result = result.filter(t => (t.tickerSource || 'scraped') === tickerSourceFilter);
    }
    if (politicianSearch.trim()) {
      const q = politicianSearch.toLowerCase();
      result = result.filter(t =>
        t.politician.toLowerCase().includes(q) ||
        t.ticker.toLowerCase().includes(q) ||
        t.issuer.toLowerCase().includes(q)
      );
    }
    const cutoff = getTimeRangeCutoff(timeRange);
    if (cutoff) {
      result = result.filter(t => {
        const d = parseTradedDate(t.traded);
        return d && d >= cutoff;
      });
    }

    if (dateFrom) {
      result = result.filter(t => {
        const d = parseTradedDate(t.traded);
        return d && d >= dateFrom;
      });
    }
    if (dateTo) {
      const endOfDay = new Date(dateTo);
      endOfDay.setHours(23, 59, 59, 999);
      result = result.filter(t => {
        const d = parseTradedDate(t.traded);
        return d && d <= endOfDay;
      });
    }

    if (sortField && sortDir) {
      result.sort((a, b) => {
        if (sortField === 'price') {
          const pa = parseFloat(a.price.replace(/[$,]/g, '')) || 0;
          const pb = parseFloat(b.price.replace(/[$,]/g, '')) || 0;
          return sortDir === 'asc' ? pa - pb : pb - pa;
        }
        const va = (a[sortField] || '').toLowerCase();
        const vb = (b[sortField] || '').toLowerCase();
        const cmp = va.localeCompare(vb);
        return sortDir === 'asc' ? cmp : -cmp;
      });
    }

    return result;
  }, [trades, partyFilter, typeFilter, chamberFilter, sizeFilter, issuerFilter, tickerSourceFilter, politicianSearch, timeRange, dateFrom, dateTo, sortField, sortDir]);

  const uniqueParties = useMemo(() =>
    [...new Set(trades.map(t => t.party).filter(p => p && p !== 'Unknown'))].sort(),
    [trades]
  );

  const uniqueSizes = useMemo(() =>
    [...new Set(trades.map(t => t.size).filter(s => s && s !== 'N/A'))].sort(),
    [trades]
  );

  const uniqueIssuers = useMemo(() =>
    [...new Set(trades.map(t => t.issuer).filter(i => i && i !== 'Unknown'))].sort(),
    [trades]
  );

  const activeFilterCount = [partyFilter, typeFilter, chamberFilter, sizeFilter, issuerFilter, tickerSourceFilter, timeRange]
    .filter(f => f !== 'all').length + (politicianSearch.trim() ? 1 : 0) + (dateFrom ? 1 : 0) + (dateTo ? 1 : 0);

  const hasActiveFilters = activeFilterCount > 0;

  const clearFilters = () => {
    setPartyFilter('all');
    setTypeFilter('all');
    setChamberFilter('all');
    setSizeFilter('all');
    setIssuerFilter('all');
    setTickerSourceFilter('all');
    setPoliticianSearch('');
    setTimeRange('all');
    setDateFrom(undefined);
    setDateTo(undefined);
  };

  const saveTradesToDb = async (tradesData: CapitolTrade[], fetchTime: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      await supabase.from('capitol_trades_snapshots').insert({
        user_id: user.id,
        trades_data: tradesData as any,
        trades_count: tradesData.length,
        fetched_at: fetchTime,
      });
      loadSnapshots();
    } catch (err) {
      console.error('Error saving trades snapshot:', err);
    }
  };

  const loadSnapshots = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('capitol_trades_snapshots')
        .select('id, trades_count, fetched_at, created_at')
        .order('fetched_at', { ascending: false })
        .limit(50);

      if (!error && data) {
        setSnapshots(data);
      }
    } catch (err) {
      console.error('Error loading snapshots:', err);
    }
  };

  const loadSnapshotTrades = async (snapshotId: string) => {
    setLoadingHistory(true);
    try {
      const { data, error } = await supabase
        .from('capitol_trades_snapshots')
        .select('trades_data, fetched_at')
        .eq('id', snapshotId)
        .single();

      if (error) throw error;
      if (data) {
        setCapitolTrades(data.trades_data as unknown as CapitolTrade[]);
        setFetchedAt(data.fetched_at);
        setViewingSnapshot(snapshotId);
        setDataSource('capitoltrades');
        setShowHistory(false);
        toast({
          title: 'Historical Snapshot Loaded',
          description: `Viewing trades from ${new Date(data.fetched_at).toLocaleString()}`,
        });
      }
    } catch (err: any) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setLoadingHistory(false);
    }
  };

  const deleteSnapshot = async (snapshotId: string) => {
    try {
      const { error } = await supabase
        .from('capitol_trades_snapshots')
        .delete()
        .eq('id', snapshotId);

      if (error) throw error;
      setSnapshots(prev => prev.filter(s => s.id !== snapshotId));
      if (viewingSnapshot === snapshotId) setViewingSnapshot(null);
      toast({ title: 'Snapshot Deleted' });
    } catch (err: any) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    }
  };

  const fetchTrades = async () => {
    setLoading(true);
    setViewingSnapshot(null);
    try {
      const { data, error } = await supabase.functions.invoke('capitol-trades');
      if (error) throw error;

      if (data?.success && data.trades) {
        setCapitolTrades(data.trades);
        setFetchedAt(data.fetchedAt);
        toast({
          title: 'Capitol Trades Loaded',
          description: `${data.trades.length} recent trades fetched from Capitol Trades`,
        });
        // Auto-save to database
        saveTradesToDb(data.trades, data.fetchedAt);
      } else {
        throw new Error(data?.error || 'No trade data returned');
      }
    } catch (err: any) {
      console.error('Error fetching Capitol Trades:', err);
      toast({
        title: 'Error Loading Trades',
        description: err.message || 'Failed to fetch Capitol Trades data',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchQuiverTrades = async () => {
    setLoadingQuiver(true);
    try {
      const { data, error } = await supabase.functions.invoke('quiver-trades');
      if (error) throw error;

      if (data?.success && data.trades) {
        setQuiverTrades(data.trades);
        setQuiverFetchedAt(data.fetchedAt);
        toast({
          title: 'QuiverQuant Trades Loaded',
          description: `${data.trades.length} recent trades fetched from QuiverQuant`,
        });
      } else {
        throw new Error(data?.error || 'No trade data returned');
      }
    } catch (err: any) {
      console.error('Error fetching QuiverQuant:', err);
      toast({
        title: 'Error Loading QuiverQuant',
        description: err.message || 'Failed to fetch QuiverQuant data',
        variant: 'destructive',
      });
    } finally {
      setLoadingQuiver(false);
    }
  };

  useEffect(() => {
    fetchTrades();
    fetchQuiverTrades();
    loadSnapshots();
  }, []);

  const getTypeBadge = (type: string) => {
    const t = type.toLowerCase();
    if (t === 'buy' || t === 'purchase') {
      return (
        <Badge className="bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30 hover:bg-emerald-500/25">
          <TrendingUp className="h-3 w-3 mr-1" />
          Buy
        </Badge>
      );
    }
    if (t === 'sell' || t === 'sale') {
      return (
        <Badge className="bg-rose-500/15 text-rose-700 dark:text-rose-400 border-rose-500/30 hover:bg-rose-500/25">
          <TrendingDown className="h-3 w-3 mr-1" />
          Sell
        </Badge>
      );
    }
    if (t === 'exchange') {
      return <Badge variant="secondary">Exchange</Badge>;
    }
    return <Badge variant="outline">{type || 'N/A'}</Badge>;
  };

  const getPartyColor = (party: string) => {
    const p = party.toLowerCase();
    if (p.includes('republican')) return 'text-rose-600 dark:text-rose-400';
    if (p.includes('democrat')) return 'text-blue-600 dark:text-blue-400';
    return 'text-muted-foreground';
  };

  const getChamberBadge = (chamber: string) => {
    if (chamber === 'House') {
      return <Badge variant="outline" className="text-xs border-amber-500/30 text-amber-700 dark:text-amber-400">House</Badge>;
    }
    if (chamber === 'Senate') {
      return <Badge variant="outline" className="text-xs border-violet-500/30 text-violet-700 dark:text-violet-400">Senate</Badge>;
    }
    return <Badge variant="outline" className="text-xs">{chamber}</Badge>;
  };

  // Reset page when filters change
  useEffect(() => { setCurrentPage(1); }, [partyFilter, typeFilter, chamberFilter, sizeFilter, issuerFilter, tickerSourceFilter, politicianSearch, sortField, sortDir]);

  const totalPages = Math.max(1, Math.ceil(filteredAndSorted.length / pageSize));
  const paginatedTrades = filteredAndSorted.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const buyCount = filteredAndSorted.filter(t => ['buy', 'purchase'].includes(t.type.toLowerCase())).length;
  const sellCount = filteredAndSorted.filter(t => ['sell', 'sale'].includes(t.type.toLowerCase())).length;

  // Chart data: trades per politician (top 10)
  const politicianChartData = useMemo(() => {
    const counts: Record<string, { buys: number; sells: number; other: number }> = {};
    filteredAndSorted.forEach(t => {
      if (!counts[t.politician]) counts[t.politician] = { buys: 0, sells: 0, other: 0 };
      const tt = t.type.toLowerCase();
      if (tt === 'buy' || tt === 'purchase') counts[t.politician].buys++;
      else if (tt === 'sell' || tt === 'sale') counts[t.politician].sells++;
      else counts[t.politician].other++;
    });
    return Object.entries(counts)
      .map(([name, v]) => ({ name, buys: v.buys, sells: v.sells, other: v.other, total: v.buys + v.sells + v.other }))
      .sort((a, b) => b.total - a.total)
      .slice(0, 10);
  }, [filteredAndSorted]);

  // Chart data: party breakdown
  const partyPieData = useMemo(() => {
    const counts: Record<string, { buys: number; sells: number }> = {};
    filteredAndSorted.forEach(t => {
      const party = t.party || 'Unknown';
      if (!counts[party]) counts[party] = { buys: 0, sells: 0 };
      const tt = t.type.toLowerCase();
      if (tt === 'buy' || tt === 'purchase') counts[party].buys++;
      else if (tt === 'sell' || tt === 'sale') counts[party].sells++;
    });
    return Object.entries(counts).map(([name, v]) => ({
      name,
      buys: v.buys,
      sells: v.sells,
      total: v.buys + v.sells,
    }));
  }, [filteredAndSorted]);

  const PARTY_COLORS: Record<string, string> = {
    Republican: '#e11d48',
    Democrat: '#2563eb',
    Unknown: '#6b7280',
    Independent: '#8b5cf6',
  };

  // Chart data: party × transaction type grouped bar
  const partyTxChartData = useMemo(() => {
    const counts: Record<string, { buy: number; sell: number; exchange: number; other: number }> = {};
    filteredAndSorted.forEach(t => {
      const party = t.party || 'Unknown';
      if (!counts[party]) counts[party] = { buy: 0, sell: 0, exchange: 0, other: 0 };
      const tt = t.type.toLowerCase();
      if (tt === 'buy' || tt === 'purchase') counts[party].buy++;
      else if (tt === 'sell' || tt === 'sale') counts[party].sell++;
      else if (tt === 'exchange') counts[party].exchange++;
      else counts[party].other++;
    });
    return Object.entries(counts)
      .map(([party, v]) => ({ party, buy: v.buy, sell: v.sell, exchange: v.exchange, other: v.other, total: v.buy + v.sell + v.exchange + v.other }))
      .sort((a, b) => b.total - a.total);
  }, [filteredAndSorted]);

  // Top bought tickers
  const topBoughtData = useMemo(() => {
    const counts: Record<string, number> = {};
    filteredAndSorted.forEach(t => {
      const tt = t.type.toLowerCase();
      if (tt === 'buy' || tt === 'purchase') {
        const ticker = (t.ticker && t.ticker.trim()) ? t.ticker.trim() : (t.issuer || 'Unknown');
        counts[ticker] = (counts[ticker] || 0) + 1;
      }
    });
    return Object.entries(counts)
      .map(([ticker, count]) => ({ ticker, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 15);
  }, [filteredAndSorted]);

  // Top sold tickers
  const topSoldData = useMemo(() => {
    const counts: Record<string, number> = {};
    filteredAndSorted.forEach(t => {
      const tt = t.type.toLowerCase();
      if (tt === 'sell' || tt === 'sale') {
        const ticker = (t.ticker && t.ticker.trim()) ? t.ticker.trim() : (t.issuer || 'Unknown');
        counts[ticker] = (counts[ticker] || 0) + 1;
      }
    });
    return Object.entries(counts)
      .map(([ticker, count]) => ({ ticker, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 15);
  }, [filteredAndSorted]);

  // Top traded tickers overall (buys + sells stacked)
  const topTradedData = useMemo(() => {
    const counts: Record<string, { buys: number; sells: number; other: number }> = {};
    filteredAndSorted.forEach(t => {
      const ticker = (t.ticker && t.ticker.trim()) ? t.ticker.trim() : (t.issuer || 'Unknown');
      if (!counts[ticker]) counts[ticker] = { buys: 0, sells: 0, other: 0 };
      const tt = t.type.toLowerCase();
      if (tt === 'buy' || tt === 'purchase') counts[ticker].buys++;
      else if (tt === 'sell' || tt === 'sale') counts[ticker].sells++;
      else counts[ticker].other++;
    });
    return Object.entries(counts)
      .map(([ticker, v]) => ({ ticker, buys: v.buys, sells: v.sells, other: v.other, total: v.buys + v.sells + v.other }))
      .sort((a, b) => b.total - a.total)
      .slice(0, 20);
  }, [filteredAndSorted]);

  // Net sentiment per ticker (buys - sells), sorted by net
  const netSentimentData = useMemo(() => {
    const counts: Record<string, { buys: number; sells: number }> = {};
    filteredAndSorted.forEach(t => {
      const ticker = (t.ticker && t.ticker.trim()) ? t.ticker.trim() : (t.issuer || 'Unknown');
      if (!counts[ticker]) counts[ticker] = { buys: 0, sells: 0 };
      const tt = t.type.toLowerCase();
      if (tt === 'buy' || tt === 'purchase') counts[ticker].buys++;
      else if (tt === 'sell' || tt === 'sale') counts[ticker].sells++;
    });
    return Object.entries(counts)
      .filter(([, v]) => v.buys + v.sells >= 2)
      .map(([ticker, v]) => ({
        ticker,
        buys: v.buys,
        sells: -v.sells,
        net: v.buys - v.sells,
      }))
      .sort((a, b) => b.net - a.net)
      .slice(0, 25);
  }, [filteredAndSorted]);


  // Timeline chart data: trades per week/month
  const timelineData = useMemo(() => {
    const buckets: Record<string, { buys: number; sells: number; other: number }> = {};
    filteredAndSorted.forEach(t => {
      const d = parseTradedDate(t.traded);
      if (!d) return;
      let key: string;
      if (timelineGranularity === 'month') {
        key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      } else {
        const day = new Date(d);
        const dayOfWeek = day.getDay();
        const diff = day.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
        day.setDate(diff);
        key = day.toISOString().slice(0, 10);
      }
      if (!buckets[key]) buckets[key] = { buys: 0, sells: 0, other: 0 };
      const tt = t.type.toLowerCase();
      if (tt === 'buy' || tt === 'purchase') buckets[key].buys++;
      else if (tt === 'sell' || tt === 'sale') buckets[key].sells++;
      else buckets[key].other++;
    });
    const sorted = Object.entries(buckets)
      .map(([period, v]) => ({ period, buys: v.buys, sells: v.sells, other: v.other, total: v.buys + v.sells + v.other, cumNet: 0 }))
      .sort((a, b) => a.period.localeCompare(b.period));
    let running = 0;
    sorted.forEach(d => { running += d.buys - d.sells; d.cumNet = running; });
    return sorted;
  }, [filteredAndSorted, timelineGranularity]);

  // Politician leaderboard data
  const leaderboardData = useMemo(() => {
    const stats: Record<string, { buys: number; sells: number; total: number; party: string; chamber: string; state: string }> = {};
    filteredAndSorted.forEach(t => {
      const name = t.politician;
      if (!name || name === 'Unknown') return;
      if (!stats[name]) stats[name] = { buys: 0, sells: 0, total: 0, party: t.party, chamber: t.chamber, state: t.state };
      stats[name].total++;
      const tt = t.type.toLowerCase();
      if (tt === 'buy' || tt === 'purchase') stats[name].buys++;
      else if (tt === 'sell' || tt === 'sale') stats[name].sells++;
    });
    return Object.entries(stats)
      .map(([name, v]) => ({ name, ...v, net: v.buys - v.sells }))
      .sort((a, b) => {
        if (leaderboardSort === 'net') return b.net - a.net;
        if (leaderboardSort === 'buys') return b.buys - a.buys;
        return b.total - a.total;
      })
      .slice(0, 20);
  }, [filteredAndSorted, leaderboardSort]);

  // Sector breakdown data based on issuer names
  const sectorBreakdownData = useMemo(() => {
    const sectorKeywords: Record<string, string[]> = {
      'Technology': ['apple', 'microsoft', 'google', 'alphabet', 'meta', 'amazon', 'nvidia', 'amd', 'intel', 'qualcomm', 'broadcom', 'adobe', 'salesforce', 'oracle', 'ibm', 'cisco', 'palantir', 'snowflake', 'crowdstrike', 'datadog', 'shopify', 'twilio', 'zoom', 'uber', 'lyft', 'snap', 'pinterest', 'roblox', 'unity', 'cloudflare', 'mongo', 'elastic', 'asml', 'tsmc', 'arm', 'micron', 'texas instruments', 'applied materials', 'lam research', 'synopsys', 'cadence', 'servicenow', 'workday', 'splunk', 'fortinet', 'palo alto', 'zscaler', 'okta', 'hubspot', 'atlassian', 'docusign'],
      'Finance': ['jpmorgan', 'jp morgan', 'goldman', 'morgan stanley', 'bank of america', 'wells fargo', 'citigroup', 'blackrock', 'schwab', 'fidelity', 'visa', 'mastercard', 'paypal', 'square', 'block', 'sofi', 'robinhood', 'coinbase', 'berkshire', 'american express', 'capital one', 'discover', 'ally', 'state street', 'northern trust', 'bnp', 'hsbc', 'barclays', 'ubs', 'credit suisse', 'aon', 'marsh', 'metlife', 'prudential', 'aflac', 'progressive', 'allstate', 'travelers', 'chubb'],
      'Healthcare': ['pfizer', 'johnson & johnson', 'unitedhealth', 'abbvie', 'merck', 'eli lilly', 'lilly', 'amgen', 'gilead', 'regeneron', 'moderna', 'biontech', 'bristol-myers', 'thermo fisher', 'danaher', 'abbott', 'medtronic', 'intuitive surgical', 'stryker', 'edwards', 'boston scientific', 'zimmer', 'align', 'hologic', 'illumina', 'astrazeneca', 'novo nordisk', 'roche', 'novartis', 'sanofi', 'gsk', 'biogen', 'vertex', 'humana', 'centene', 'cigna', 'cvs', 'anthem', 'elevance'],
      'Energy': ['exxon', 'chevron', 'conocophillips', 'schlumberger', 'halliburton', 'baker hughes', 'pioneer', 'devon', 'diamondback', 'marathon', 'occidental', 'phillips 66', 'valero', 'enbridge', 'kinder morgan', 'williams', 'oneok', 'coterra', 'eog', 'hess', 'bp', 'shell', 'totalenergies', 'nextera', 'duke energy', 'southern company', 'dominion', 'sempra', 'aes', 'first solar', 'enphase', 'sunrun', 'plug power'],
      'Consumer': ['walmart', 'costco', 'target', 'home depot', 'lowes', "lowe's", 'nike', 'starbucks', 'mcdonald', 'coca-cola', 'pepsi', 'procter', 'p&g', 'colgate', 'estee lauder', 'tesla', 'ford', 'gm', 'general motors', 'toyota', 'rivian', 'lucid', 'disney', 'netflix', 'comcast', 'warner', 'paramount', 'spotify', 'airbnb', 'booking', 'marriott', 'hilton', 'yum', 'chipotle', 'domino', 'kroger', 'albertsons', 'dollar general', 'dollar tree', 'ross', 'tjx', 'burlington', 'gap', 'lululemon', 'ralph lauren', 'tapestry', 'capri'],
      'Industrials': ['boeing', 'lockheed', 'raytheon', 'rtx', 'northrop', 'general dynamics', 'l3harris', 'caterpillar', 'deere', 'honeywell', '3m', 'ge', 'general electric', 'siemens', 'union pacific', 'csx', 'norfolk southern', 'fedex', 'ups', 'delta', 'united airlines', 'american airlines', 'southwest', 'emerson', 'parker', 'illinois tool', 'waste management', 'republic services', 'jacobs', 'aecom', 'transdigm', 'howmet', 'carrier', 'otis', 'trane'],
      'Real Estate': ['american tower', 'prologis', 'equinix', 'crown castle', 'realty income', 'digital realty', 'simon property', 'vornado', 'boston properties', 'alexandria', 'welltower', 'ventas', 'public storage', 'extra space', 'iron mountain', 'weyerhaeuser', 'kimco', 'regency', 'invitation homes', 'macerich'],
      'Materials': ['freeport', 'newmont', 'nucor', 'alcoa', 'cf industries', 'mosaic', 'corteva', 'dow', 'dupont', 'air products', 'linde', 'sherwin-williams', 'ppg', 'ecolab', 'international paper', 'ball', 'packaging', 'vulcan', 'martin marietta', 'steel dynamics'],
      'Telecom': ['at&t', 'verizon', 't-mobile', 'charter', 'lumen', 'frontier', 'dish'],
    };

    const classify = (issuer: string): string => {
      const lower = issuer.toLowerCase();
      for (const [sector, keywords] of Object.entries(sectorKeywords)) {
        if (keywords.some(kw => lower.includes(kw))) return sector;
      }
      return 'Other';
    };

    const sectorStats: Record<string, { buys: number; sells: number; total: number; politicians: Set<string> }> = {};
    filteredAndSorted.forEach(t => {
      const sector = classify(t.issuer);
      if (!sectorStats[sector]) sectorStats[sector] = { buys: 0, sells: 0, total: 0, politicians: new Set() };
      sectorStats[sector].total++;
      sectorStats[sector].politicians.add(t.politician);
      const tt = t.type.toLowerCase();
      if (tt === 'buy' || tt === 'purchase') sectorStats[sector].buys++;
      else if (tt === 'sell' || tt === 'sale') sectorStats[sector].sells++;
    });

    return Object.entries(sectorStats)
      .map(([sector, v]) => ({ sector, ...v, net: v.buys - v.sells, politicians: v.politicians.size }))
      .sort((a, b) => b.total - a.total);
  }, [filteredAndSorted]);

  const SortableHeader = ({ field, children }: { field: SortField; children: React.ReactNode }) => (
    <TableHead
      className="font-semibold cursor-pointer select-none hover:text-foreground transition-colors"
      onClick={() => toggleSort(field)}
    >
      <div className="flex items-center gap-1">
        {children}
        {sortField === field ? (
          sortDir === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
        ) : (
          <ArrowUpDown className="h-3 w-3 opacity-30" />
        )}
      </div>
    </TableHead>
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b bg-card/50 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/')}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="flex items-center gap-2">
              <Landmark className="h-5 w-5 text-amber-600 dark:text-amber-400" />
              <h1 className="text-lg font-bold text-foreground">Capitol Trades</h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {activeFetchedAt && (
              <span className="text-xs text-muted-foreground hidden sm:inline">
                {viewingSnapshot ? '📂 Viewing snapshot: ' : 'Last updated: '}
                {new Date(activeFetchedAt).toLocaleString()}
              </span>
            )}
            {viewingSnapshot && (
              <Button variant="outline" size="sm" onClick={fetchTrades} className="text-xs">
                ← Live Data
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={() => setShowHistory(!showHistory)} disabled={loadingHistory}>
              <History className="h-4 w-4 mr-1" />
              History
              {snapshots.length > 0 && (
                <Badge variant="secondary" className="ml-1 text-xs h-5 px-1.5">{snapshots.length}</Badge>
              )}
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={filteredAndSorted.length === 0}
              onClick={() => {
                const headers = ['Politician','Party','Chamber','State','Issuer','Ticker','Type','Size','Price','Traded','Published','Owner','Trade URL'];
                const rows = filteredAndSorted.map(t => [t.politician,t.party,t.chamber,t.state,t.issuer,t.ticker,t.type,t.size,t.price,t.traded,t.published,t.owner,t.tradeUrl]
                  .map(v => `"${(v || '').replace(/"/g, '""')}"`)
                  .join(','));
                const csv = [headers.join(','), ...rows].join('\n');
                const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `capitol-trades-${dataSource}-${new Date().toISOString().slice(0,10)}.csv`;
                a.click();
                URL.revokeObjectURL(url);
              }}
            >
              <Download className="h-4 w-4 mr-1" />
              CSV
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={filteredAndSorted.length === 0}
              onClick={() => {
                const headers = ['Politician','Party','Chamber','State','Issuer','Ticker','Type','Size','Price','Traded','Published','Owner','Trade URL'];
                const data = filteredAndSorted.map(t => [t.politician,t.party,t.chamber,t.state,t.issuer,t.ticker,t.type,t.size,t.price,t.traded,t.published,t.owner,t.tradeUrl]);
                const ws = XLSX.utils.aoa_to_sheet([headers, ...data]);
                ws['!cols'] = headers.map(() => ({ wch: 18 }));

                for (let c = 0; c < headers.length; c++) {
                  const cell = ws[XLSX.utils.encode_cell({ r: 0, c })];
                  if (cell) cell.s = { font: { bold: true, color: { rgb: 'FFFFFF' } }, fill: { fgColor: { rgb: '1F2937' } } };
                }

                filteredAndSorted.forEach((t, i) => {
                  const rowIdx = i + 1;
                  const type = (t.type || '').toLowerCase();
                  let bg: string | null = null;
                  if (type === 'buy' || type === 'purchase') bg = 'DCFCE7';
                  else if (type === 'sell' || type === 'sale') bg = 'FEE2E2';
                  if (bg) {
                    for (let c = 0; c < headers.length; c++) {
                      const cell = ws[XLSX.utils.encode_cell({ r: rowIdx, c })];
                      if (cell) cell.s = { fill: { fgColor: { rgb: bg } } };
                    }
                  }
                });

                const wb = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(wb, ws, 'Capitol Trades');
                XLSX.writeFile(wb, `capitol-trades-${dataSource}-${new Date().toISOString().slice(0,10)}.xlsx`);
              }}
            >
              <Download className="h-4 w-4 mr-1" />
              Excel
            </Button>
            <Button variant="outline" size="sm" onClick={() => {
              if (dataSource === 'capitoltrades') fetchTrades();
              else fetchQuiverTrades();
            }} disabled={isActiveLoading}>
              <RefreshCw className={`h-4 w-4 mr-1 ${isActiveLoading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button variant="outline" size="sm" asChild>
              <a href={dataSource === 'capitoltrades' ? 'https://www.capitoltrades.com/trades' : 'https://www.quiverquant.com/congresstrading/'} target="_blank" rel="noopener noreferrer">
                <ExternalLink className="h-4 w-4 mr-1" />
                Source
              </a>
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Data Source Toggle */}
        <Card className="border-amber-500/20">
          <CardContent className="p-4">
            <div className="flex items-center gap-4 flex-wrap">
              <div className="flex items-center gap-2">
                <Globe className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                <span className="text-sm font-medium text-foreground">Data Source</span>
              </div>
              <Tabs value={dataSource} onValueChange={(v) => { setDataSource(v as DataSource); setCurrentPage(1); }}>
                <TabsList>
                  <TabsTrigger value="capitoltrades" className="gap-1.5">
                    <Landmark className="h-3.5 w-3.5" />
                    CapitolTrades
                    {capitolTrades.length > 0 && (
                      <Badge variant="secondary" className="ml-1 text-xs h-5 px-1.5">{capitolTrades.length}</Badge>
                    )}
                    {loading && <RefreshCw className="h-3 w-3 animate-spin ml-1" />}
                  </TabsTrigger>
                  <TabsTrigger value="quiver" className="gap-1.5">
                    <BarChart3 className="h-3.5 w-3.5" />
                    QuiverQuant
                    {quiverTrades.length > 0 && (
                      <Badge variant="secondary" className="ml-1 text-xs h-5 px-1.5">{quiverTrades.length}</Badge>
                    )}
                    {loadingQuiver && <RefreshCw className="h-3 w-3 animate-spin ml-1" />}
                  </TabsTrigger>
                  <TabsTrigger value="combined" className="gap-1.5" disabled={capitolTrades.length === 0 && quiverTrades.length === 0}>
                    <Globe className="h-3.5 w-3.5" />
                    Combined
                    {combinedTrades.length > 0 && (
                      <Badge variant="secondary" className="ml-1 text-xs h-5 px-1.5">{combinedTrades.length}</Badge>
                    )}
                  </TabsTrigger>
                </TabsList>
              </Tabs>
              <span className="text-xs text-muted-foreground">
                {dataSource === 'capitoltrades'
                  ? 'Scraped from capitoltrades.com — includes pricing & owner data'
                  : dataSource === 'quiver'
                    ? 'Scraped from quiverquant.com — includes excess return & trade descriptions'
                    : `Merged from both sources — ${combinedTrades.filter(t => t._source === 'both').length} overlapping trades deduplicated`}
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Summary Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Card className="border-amber-500/20">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-foreground">{filteredAndSorted.length}</p>
              <p className="text-xs text-muted-foreground">
                {hasActiveFilters ? `of ${trades.length} ` : ''}Trades
              </p>
            </CardContent>
          </Card>
          <Card className="border-emerald-500/20">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">{buyCount}</p>
              <p className="text-xs text-muted-foreground">Buys</p>
            </CardContent>
          </Card>
          <Card className="border-rose-500/20">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-rose-600 dark:text-rose-400">{sellCount}</p>
              <p className="text-xs text-muted-foreground">Sells</p>
            </CardContent>
          </Card>
          <Card className="border-violet-500/20">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-foreground">
                {new Set(filteredAndSorted.map(t => t.politician)).size}
              </p>
              <p className="text-xs text-muted-foreground">Politicians</p>
            </CardContent>
          </Card>
        </div>

        {/* Charts Section */}
        {filteredAndSorted.length > 0 && (
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  <BarChart3 className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                  Trade Volume Breakdown
                </CardTitle>
                <div className="flex gap-1">
                  <Button
                    variant={chartMode === 'politician' ? 'default' : 'outline'}
                    size="sm"
                    className="text-xs h-7"
                    onClick={() => setChartMode('politician')}
                  >
                    By Politician
                  </Button>
                  <Button
                    variant={chartMode === 'party' ? 'default' : 'outline'}
                    size="sm"
                    className="text-xs h-7"
                    onClick={() => setChartMode('party')}
                  >
                    By Party
                  </Button>
                </div>
              </div>
              <CardDescription>
                {chartMode === 'politician' ? 'Top 10 most active politicians by trade count' : 'Trade distribution by political party'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {chartMode === 'politician' ? (
                <ResponsiveContainer width="100%" height={320}>
                  <BarChart data={politicianChartData} layout="vertical" margin={{ left: 10, right: 20, top: 5, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.15} />
                    <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12 }} />
                    <YAxis
                      dataKey="name"
                      type="category"
                      width={120}
                      tick={{ fontSize: 11 }}
                    />
                    <Tooltip
                      contentStyle={{
                        background: 'hsl(var(--card))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px',
                        fontSize: '12px',
                      }}
                    />
                    <Bar dataKey="buys" stackId="a" fill="#10b981" name="Buys" radius={[0, 0, 0, 0]} />
                    <Bar dataKey="sells" stackId="a" fill="#f43f5e" name="Sells" radius={[0, 4, 4, 0]} />
                    <Legend wrapperStyle={{ fontSize: '12px' }} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <ResponsiveContainer width="100%" height={280}>
                    <PieChart>
                      <Pie
                        data={partyPieData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        dataKey="total"
                        nameKey="name"
                        label={({ name, total }) => `${name}: ${total}`}
                        labelLine={false}
                      >
                        {partyPieData.map((entry) => (
                          <Cell key={entry.name} fill={PARTY_COLORS[entry.name] || '#6b7280'} />
                        ))}
                      </Pie>
                      <Tooltip
                        contentStyle={{
                          background: 'hsl(var(--card))',
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px',
                          fontSize: '12px',
                        }}
                      />
                      <Legend wrapperStyle={{ fontSize: '12px' }} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="flex flex-col justify-center gap-3">
                    {partyPieData.map(p => (
                      <div key={p.name} className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: PARTY_COLORS[p.name] || '#6b7280' }} />
                          <span className="text-sm font-medium text-foreground">{p.name}</span>
                        </div>
                        <div className="flex gap-4 text-xs">
                          <span className="text-emerald-600 dark:text-emerald-400">{p.buys} buys</span>
                          <span className="text-rose-600 dark:text-rose-400">{p.sells} sells</span>
                          <span className="font-semibold text-foreground">{p.total} total</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Party × Transaction Type Chart */}
        {filteredAndSorted.length > 0 && partyTxChartData.length > 0 && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <BarChart3 className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                Trade Volume by Party &amp; Transaction Type
              </CardTitle>
              <CardDescription>
                Grouped breakdown of buys, sells, and exchanges per political party
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={partyTxChartData} margin={{ left: 10, right: 20, top: 5, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.15} />
                  <XAxis dataKey="party" tick={{ fontSize: 12 }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                  <Tooltip
                    contentStyle={{
                      background: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px',
                      fontSize: '12px',
                    }}
                  />
                  <Legend wrapperStyle={{ fontSize: '12px' }} />
                  <Bar dataKey="buy" fill="#10b981" name="Buys" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="sell" fill="#f43f5e" name="Sells" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="exchange" fill="#f59e0b" name="Exchange" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="other" fill="#6b7280" name="Other" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}

        {/* Top Traded Tickers Ranking */}
        {filteredAndSorted.length > 0 && topTradedData.length > 0 && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <BarChart3 className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                Top Traded Tickers by Congress
              </CardTitle>
              <CardDescription>Top 20 most actively traded stocks — buys vs sells stacked</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={Math.max(300, topTradedData.length * 28)}>
                <BarChart data={topTradedData} layout="vertical" margin={{ left: 5, right: 20, top: 5, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.15} />
                  <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                  <YAxis dataKey="ticker" type="category" width={80} tick={{ fontSize: 11 }} />
                  <Tooltip
                    contentStyle={{
                      background: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px',
                      fontSize: '12px',
                    }}
                  />
                  <Legend wrapperStyle={{ fontSize: '12px' }} />
                  <Bar dataKey="buys" stackId="a" fill="#10b981" name="Buys" />
                  <Bar dataKey="sells" stackId="a" fill="#f43f5e" name="Sells" />
                  <Bar dataKey="other" stackId="a" fill="#6b7280" name="Other" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}

        {/* Top Bought & Top Sold Histograms */}
        {filteredAndSorted.length > 0 && (topBoughtData.length > 0 || topSoldData.length > 0) && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Most Purchased */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <TrendingUp className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                  Most Purchased Stocks
                </CardTitle>
                <CardDescription>Top tickers by buy volume (filtered)</CardDescription>
              </CardHeader>
              <CardContent>
                {topBoughtData.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-8">No buy trades in current view</p>
                ) : (
                  <ResponsiveContainer width="100%" height={Math.max(200, topBoughtData.length * 32)}>
                    <BarChart data={topBoughtData} layout="vertical" margin={{ left: 5, right: 20, top: 5, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" opacity={0.15} />
                      <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                      <YAxis dataKey="ticker" type="category" width={80} tick={{ fontSize: 11 }} />
                      <Tooltip
                        contentStyle={{
                          background: 'hsl(var(--card))',
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px',
                          fontSize: '12px',
                        }}
                      />
                      <Bar dataKey="count" fill="#10b981" name="Buy Trades" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>

            {/* Most Sold */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <TrendingDown className="h-4 w-4 text-rose-600 dark:text-rose-400" />
                  Most Sold Stocks
                </CardTitle>
                <CardDescription>Top tickers by sell volume (filtered)</CardDescription>
              </CardHeader>
              <CardContent>
                {topSoldData.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-8">No sell trades in current view</p>
                ) : (
                  <ResponsiveContainer width="100%" height={Math.max(200, topSoldData.length * 32)}>
                    <BarChart data={topSoldData} layout="vertical" margin={{ left: 5, right: 20, top: 5, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" opacity={0.15} />
                      <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                      <YAxis dataKey="ticker" type="category" width={80} tick={{ fontSize: 11 }} />
                      <Tooltip
                        contentStyle={{
                          background: 'hsl(var(--card))',
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px',
                          fontSize: '12px',
                        }}
                      />
                      <Bar dataKey="count" fill="#f43f5e" name="Sell Trades" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>
          </div>
        )}


        {/* Net Congressional Sentiment Chart */}
        {filteredAndSorted.length > 0 && netSentimentData.length > 0 && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <BarChart3 className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                Net Congressional Sentiment by Ticker
              </CardTitle>
              <CardDescription>
                Buy vs sell comparison — positive = net buying, negative = net selling (min 2 trades)
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={Math.max(300, netSentimentData.length * 28)}>
                <BarChart data={netSentimentData} layout="vertical" margin={{ left: 5, right: 20, top: 5, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.15} />
                  <XAxis type="number" tick={{ fontSize: 11 }} />
                  <YAxis dataKey="ticker" type="category" width={80} tick={{ fontSize: 11 }} />
                  <Tooltip
                    contentStyle={{
                      background: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px',
                      fontSize: '12px',
                    }}
                    formatter={(value: number, name: string) => [
                      Math.abs(value),
                      name === 'buys' ? 'Buys' : 'Sells',
                    ]}
                  />
                  <Legend wrapperStyle={{ fontSize: '12px' }} />
                  <Bar dataKey="buys" stackId="a" fill="#10b981" name="Buys" />
                  <Bar dataKey="sells" stackId="a" fill="#f43f5e" name="Sells" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}

        {/* Trade Volume Timeline */}
        {filteredAndSorted.length > 0 && timelineData.length > 1 && (
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  <TrendingUp className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                  Trade Volume Timeline
                </CardTitle>
                <div className="flex gap-1">
                  <Button
                    variant={timelineGranularity === 'week' ? 'default' : 'outline'}
                    size="sm"
                    className="text-xs h-7"
                    onClick={() => setTimelineGranularity('week')}
                  >
                    Weekly
                  </Button>
                  <Button
                    variant={timelineGranularity === 'month' ? 'default' : 'outline'}
                    size="sm"
                    className="text-xs h-7"
                    onClick={() => setTimelineGranularity('month')}
                  >
                    Monthly
                  </Button>
                </div>
              </div>
              <CardDescription>
                Number of congressional trades per {timelineGranularity} over time (buys vs sells)
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={320}>
                <ComposedChart data={timelineData} margin={{ left: 10, right: 20, top: 5, bottom: 5 }}>
                  <defs>
                    <linearGradient id="buyGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="sellGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#f43f5e" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#f43f5e" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.15} />
                  <XAxis
                    dataKey="period"
                    tick={{ fontSize: 11 }}
                    tickFormatter={(v: string) =>
                      timelineGranularity === 'month'
                        ? v
                        : `W ${v.slice(5)}`
                    }
                    interval="preserveStartEnd"
                  />
                  <YAxis yAxisId="left" allowDecimals={false} tick={{ fontSize: 12 }} />
                  <YAxis yAxisId="right" orientation="right" allowDecimals={false} tick={{ fontSize: 12, fill: '#f59e0b' }} />
                  <Tooltip
                    contentStyle={{
                      background: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px',
                      fontSize: '12px',
                    }}
                    labelFormatter={(v: string) =>
                      timelineGranularity === 'month' ? `Month: ${v}` : `Week of ${v}`
                    }
                  />
                  <Legend wrapperStyle={{ fontSize: '12px' }} />
                  <Area yAxisId="left" type="monotone" dataKey="buys" stroke="#10b981" fill="url(#buyGrad)" name="Buys" strokeWidth={2} />
                  <Area yAxisId="left" type="monotone" dataKey="sells" stroke="#f43f5e" fill="url(#sellGrad)" name="Sells" strokeWidth={2} />
                  <Line yAxisId="right" type="monotone" dataKey="cumNet" stroke="#f59e0b" strokeWidth={2} dot={false} name="Cumulative Net (Buy−Sell)" />
                </ComposedChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}

        {/* Politician Leaderboard */}
        {filteredAndSorted.length > 0 && leaderboardData.length > 0 && (
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Trophy className="h-4 w-4 text-amber-500" />
                    Politician Leaderboard
                  </CardTitle>
                  <CardDescription>Top congress members ranked by {leaderboardSort === 'total' ? 'trade frequency' : leaderboardSort === 'net' ? 'net buying activity' : 'buy count'}</CardDescription>
                </div>
                <div className="flex items-center gap-1 rounded-lg border p-0.5">
                  {([['total', 'Total'], ['net', 'Net Buy'], ['buys', 'Buys']] as const).map(([key, label]) => (
                    <Button
                      key={key}
                      variant={leaderboardSort === key ? 'default' : 'ghost'}
                      size="sm"
                      className="h-7 px-2.5 text-xs"
                      onClick={() => setLeaderboardSort(key)}
                    >
                      {label}
                    </Button>
                  ))}
                </div>
              </div>
            </CardHeader>
            <CardContent className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10 text-center">#</TableHead>
                    <TableHead>Politician</TableHead>
                    <TableHead className="text-center">Party</TableHead>
                    <TableHead className="text-center">Chamber</TableHead>
                    <TableHead className="text-center">Total</TableHead>
                    <TableHead className="text-center">Buys</TableHead>
                    <TableHead className="text-center">Sells</TableHead>
                    <TableHead className="text-center">Net (B−S)</TableHead>
                    <TableHead className="w-20">Net Bar</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {leaderboardData.map((p, i) => {
                    const maxAbs = Math.max(...leaderboardData.map(d => Math.abs(d.net)), 1);
                    const pct = Math.round((Math.abs(p.net) / maxAbs) * 100);
                    return (
                      <TableRow
                        key={p.name}
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => setSelectedPolitician(p.name)}
                      >
                        <TableCell className="text-center font-mono text-xs text-muted-foreground">
                          {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : i + 1}
                        </TableCell>
                        <TableCell className="font-medium text-sm">
                          {p.name}
                          {p.state && <span className="ml-1.5 text-xs text-muted-foreground">({p.state})</span>}
                        </TableCell>
                        <TableCell className={`text-center text-xs font-medium ${
                          p.party.toLowerCase().includes('republican') ? 'text-rose-600 dark:text-rose-400' :
                          p.party.toLowerCase().includes('democrat') ? 'text-blue-600 dark:text-blue-400' :
                          'text-muted-foreground'
                        }`}>
                          {p.party.slice(0, 3).toUpperCase()}
                        </TableCell>
                        <TableCell className="text-center text-xs">{p.chamber}</TableCell>
                        <TableCell className="text-center font-semibold text-sm">{p.total}</TableCell>
                        <TableCell className="text-center text-sm text-emerald-600 dark:text-emerald-400">{p.buys}</TableCell>
                        <TableCell className="text-center text-sm text-rose-600 dark:text-rose-400">{p.sells}</TableCell>
                        <TableCell className={`text-center font-semibold text-sm ${
                          p.net > 0 ? 'text-emerald-600 dark:text-emerald-400' :
                          p.net < 0 ? 'text-rose-600 dark:text-rose-400' :
                          'text-muted-foreground'
                        }`}>
                          {p.net > 0 ? '+' : ''}{p.net}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1 w-20">
                            <div className="flex-1 h-2.5 rounded-full bg-muted overflow-hidden">
                              <div
                                className={`h-full rounded-full ${p.net >= 0 ? 'bg-emerald-500' : 'bg-rose-500'}`}
                                style={{ width: `${pct}%` }}
                              />
                            </div>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {/* Sector Breakdown Chart */}
        {filteredAndSorted.length > 0 && sectorBreakdownData.length > 0 && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <BarChart3 className="h-4 w-4 text-violet-500" />
                Sector Breakdown
              </CardTitle>
              <CardDescription>Industries most actively traded by congress members</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Bar chart */}
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={sectorBreakdownData.filter(s => s.sector !== 'Other').slice(0, 10)} layout="vertical" margin={{ left: 10, right: 20, top: 5, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                      <XAxis type="number" tick={{ fontSize: 11 }} className="fill-muted-foreground" />
                      <YAxis dataKey="sector" type="category" tick={{ fontSize: 11 }} width={90} className="fill-muted-foreground" />
                      <Tooltip contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px', fontSize: 12 }} />
                      <Legend />
                      <Bar dataKey="buys" fill="hsl(142, 71%, 45%)" name="Buys" stackId="stack" radius={[0, 0, 0, 0]} />
                      <Bar dataKey="sells" fill="hsl(346, 77%, 50%)" name="Sells" stackId="stack" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                {/* Stats table */}
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="font-semibold">Sector</TableHead>
                        <TableHead className="text-center font-semibold">Trades</TableHead>
                        <TableHead className="text-center font-semibold">Buys</TableHead>
                        <TableHead className="text-center font-semibold">Sells</TableHead>
                        <TableHead className="text-center font-semibold">Net</TableHead>
                        <TableHead className="text-center font-semibold">Politicians</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {sectorBreakdownData.map(s => (
                        <TableRow key={s.sector}>
                          <TableCell className="font-medium text-sm">{s.sector}</TableCell>
                          <TableCell className="text-center text-sm font-semibold">{s.total}</TableCell>
                          <TableCell className="text-center text-sm text-emerald-600 dark:text-emerald-400">{s.buys}</TableCell>
                          <TableCell className="text-center text-sm text-rose-600 dark:text-rose-400">{s.sells}</TableCell>
                          <TableCell className={`text-center font-semibold text-sm ${
                            s.net > 0 ? 'text-emerald-600 dark:text-emerald-400' :
                            s.net < 0 ? 'text-rose-600 dark:text-rose-400' :
                            'text-muted-foreground'
                          }`}>
                            {s.net > 0 ? '+' : ''}{s.net}
                          </TableCell>
                          <TableCell className="text-center text-sm text-muted-foreground">{s.politicians}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </CardContent>
          </Card>
        )}


        <Card className="border-muted">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <Filter className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium text-foreground">Filters</span>
              {hasActiveFilters && (
                <Badge variant="secondary" className="text-xs">{activeFilterCount} active</Badge>
              )}
              {hasActiveFilters && (
                <Button variant="ghost" size="sm" onClick={clearFilters} className="h-7 text-xs gap-1 ml-auto">
                  <X className="h-3 w-3" /> Clear all
                </Button>
              )}
            </div>
            {/* Time Range Presets */}
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <Clock className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="text-xs text-muted-foreground">Time range:</span>
              {[
                { label: 'All', value: 'all' },
                { label: '30 Days', value: '30d' },
                { label: '3 Months', value: '3m' },
                { label: '6 Months', value: '6m' },
                { label: '1 Year', value: '1y' },
              ].map(opt => (
                <Button
                  key={opt.value}
                  variant={timeRange === opt.value ? 'default' : 'outline'}
                  size="sm"
                  className="h-7 text-xs px-3"
                  onClick={() => setTimeRange(opt.value)}
                >
                  {opt.label}
                </Button>
              ))}
            </div>
            {/* Custom Date Range */}
            <DateRangeFilter
              fromDate={dateFrom}
              toDate={dateTo}
              onFromChange={(d) => { setDateFrom(d); if (d) setTimeRange('all'); }}
              onToChange={(d) => { setDateTo(d); if (d) setTimeRange('all'); }}
              className="mb-3"
            />
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              {/* Search */}
              <div className="col-span-2 sm:col-span-1 lg:col-span-1">
                <label className="text-xs text-muted-foreground mb-1 block">Search</label>
                <div className="relative">
                  <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
                  <Input
                    placeholder="Politician, ticker…"
                    value={politicianSearch}
                    onChange={(e) => setPoliticianSearch(e.target.value)}
                    className="h-9 text-sm pl-8"
                  />
                </div>
              </div>

              {/* Political Party */}
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Political Party</label>
                <Select value={partyFilter} onValueChange={setPartyFilter}>
                  <SelectTrigger className="h-9 text-sm">
                    <SelectValue placeholder="All Parties" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Parties</SelectItem>
                    {uniqueParties.map(p => (
                      <SelectItem key={p} value={p.toLowerCase()}>{p}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Transaction Type */}
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Transaction Type</label>
                <Select value={typeFilter} onValueChange={setTypeFilter}>
                  <SelectTrigger className="h-9 text-sm">
                    <SelectValue placeholder="All Types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="buy">Buy</SelectItem>
                    <SelectItem value="sell">Sell</SelectItem>
                    <SelectItem value="exchange">Exchange</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Chamber */}
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Chamber</label>
                <Select value={chamberFilter} onValueChange={setChamberFilter}>
                  <SelectTrigger className="h-9 text-sm">
                    <SelectValue placeholder="All Chambers" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Chambers</SelectItem>
                    <SelectItem value="house">House</SelectItem>
                    <SelectItem value="senate">Senate</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Trade Size */}
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Trade Size</label>
                <Select value={sizeFilter} onValueChange={setSizeFilter}>
                  <SelectTrigger className="h-9 text-sm">
                    <SelectValue placeholder="All Sizes" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Sizes</SelectItem>
                    {uniqueSizes.map(s => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Issuer */}
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Issuer</label>
                <Select value={issuerFilter} onValueChange={setIssuerFilter}>
                  <SelectTrigger className="h-9 text-sm">
                    <SelectValue placeholder="All Issuers" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Issuers</SelectItem>
                    {uniqueIssuers.map(i => (
                      <SelectItem key={i} value={i}>{i}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Ticker Source */}
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Ticker Source</label>
                <Select value={tickerSourceFilter} onValueChange={setTickerSourceFilter}>
                  <SelectTrigger className="h-9 text-sm">
                    <SelectValue placeholder="All Sources" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Sources</SelectItem>
                    <SelectItem value="scraped">✓ Scraped (verified)</SelectItem>
                    <SelectItem value="fmp">F FMP-resolved</SelectItem>
                    <SelectItem value="abbreviated">~ Abbreviated (audit)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Trades Table */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Landmark className="h-4 w-4 text-amber-600 dark:text-amber-400" />
              Most Recent {dataSource === 'capitoltrades' ? 'Capitol' : 'QuiverQuant'} Trades
            </CardTitle>
            <CardDescription>
              {dataSource === 'capitoltrades'
                ? 'Real-time congressional stock trading disclosures from capitoltrades.com'
                : 'Congressional stock trading data from quiverquant.com with excess return tracking'}
            </CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            {isActiveLoading ? (
              <div className="p-6 space-y-3">
                {Array.from({ length: 8 }).map((_, i) => (
                  <div key={i} className="flex gap-4 items-center">
                    <Skeleton className="h-5 w-28" />
                    <Skeleton className="h-5 w-36" />
                    <Skeleton className="h-5 w-16" />
                    <Skeleton className="h-5 w-20" />
                    <Skeleton className="h-5 w-16" />
                    <Skeleton className="h-5 w-20" />
                  </div>
                ))}
              </div>
            ) : filteredAndSorted.length === 0 ? (
              <div className="p-12 text-center text-muted-foreground">
                <Landmark className="h-12 w-12 mx-auto mb-3 opacity-30" />
                <p className="font-medium">{hasActiveFilters ? 'No trades match filters' : 'No trades found'}</p>
                <p className="text-sm mt-1">
                  {hasActiveFilters ? (
                    <Button variant="link" size="sm" onClick={clearFilters} className="text-sm p-0 h-auto">Clear filters</Button>
                  ) : 'Click Refresh to fetch the latest data'}
                </p>
              </div>
            ) : (
              <>
                <div className="overflow-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/30">
                        {dataSource === 'combined' && <TableHead className="font-semibold w-[70px]">Source</TableHead>}
                        <SortableHeader field="politician">Politician</SortableHeader>
                        <SortableHeader field="party">Party</SortableHeader>
                        <TableHead className="font-semibold">Chamber</TableHead>
                        <TableHead className="font-semibold">Traded Issuer</TableHead>
                        <SortableHeader field="ticker">Ticker</SortableHeader>
                        <SortableHeader field="type">Type</SortableHeader>
                        <TableHead className="font-semibold">Size</TableHead>
                        {(dataSource === 'capitoltrades' || dataSource === 'combined') && <SortableHeader field="price">Price</SortableHeader>}
                        <SortableHeader field="traded">Traded</SortableHeader>
                        <TableHead className="font-semibold">Published</TableHead>
                        {(dataSource === 'capitoltrades' || dataSource === 'combined') && <TableHead className="font-semibold">Owner</TableHead>}
                        {dataSource === 'quiver' && <TableHead className="font-semibold">Return</TableHead>}
                        {dataSource === 'quiver' && <TableHead className="font-semibold">Description</TableHead>}
                        <TableHead className="font-semibold">Detail</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {paginatedTrades.map((trade, idx) => {
                        const qt = trade as any;
                        return (
                        <TableRow
                          key={idx}
                          className={
                            trade.type.toLowerCase() === 'buy' || trade.type.toLowerCase() === 'purchase'
                              ? 'bg-emerald-500/[0.03] hover:bg-emerald-500/[0.07]'
                              : trade.type.toLowerCase() === 'sell' || trade.type.toLowerCase() === 'sale'
                                ? 'bg-rose-500/[0.03] hover:bg-rose-500/[0.07]'
                                : 'hover:bg-muted/50'
                          }
                        >
                          {dataSource === 'combined' && (
                            <TableCell>
                              {trade._source === 'both' ? (
                                <Badge className="bg-violet-500/15 text-violet-700 dark:text-violet-400 border-violet-500/30 text-[9px] px-1.5">Both</Badge>
                              ) : trade._source === 'capitoltrades' ? (
                                <Badge className="bg-blue-500/15 text-blue-700 dark:text-blue-400 border-blue-500/30 text-[9px] px-1.5">CT</Badge>
                              ) : (
                                <Badge className="bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30 text-[9px] px-1.5">QQ</Badge>
                              )}
                            </TableCell>
                          )}
                          <TableCell className="font-medium text-foreground whitespace-nowrap">
                            <button
                              onClick={() => setSelectedPolitician(trade.politician)}
                              className="text-left hover:text-primary hover:underline underline-offset-2 transition-colors cursor-pointer"
                            >
                              {trade.politician}
                            </button>
                          </TableCell>
                          <TableCell><span className={`font-medium text-sm ${getPartyColor(trade.party)}`}>{trade.party}</span></TableCell>
                          <TableCell>{getChamberBadge(trade.chamber)}</TableCell>
                          <TableCell className="font-medium text-foreground max-w-[200px] truncate">{trade.issuer}</TableCell>
                          <TableCell>
                            {(trade.ticker && trade.ticker.trim()) ? (
                              <div className="flex items-center gap-1.5">
                                <button
                                  onClick={() => setSelectedTicker({ ticker: trade.ticker.trim(), issuer: trade.issuer })}
                                  className="hover:text-primary hover:underline underline-offset-2 transition-colors cursor-pointer"
                                >
                                  <code className="text-xs bg-muted px-1.5 py-0.5 rounded font-mono">{trade.ticker.trim()}</code>
                                </button>
                                {trade.tickerSource === 'fmp' && (
                                  <span title="Resolved via FMP API lookup" className="inline-flex items-center justify-center h-4 w-4 rounded-full bg-amber-500/15 text-amber-600 dark:text-amber-400 text-[8px] font-bold shrink-0 cursor-help">F</span>
                                )}
                                {trade.tickerSource === 'abbreviated' && (
                                  <span title="Abbreviated from issuer name — may be inaccurate" className="inline-flex items-center justify-center h-4 w-4 rounded-full bg-rose-500/15 text-rose-600 dark:text-rose-400 text-[8px] font-bold shrink-0 cursor-help">~</span>
                                )}
                              </div>
                            ) : (
                              <code className="text-xs bg-muted px-1.5 py-0.5 rounded font-mono text-muted-foreground">{trade.issuer || 'N/A'}</code>
                            )}
                          </TableCell>
                          <TableCell>{getTypeBadge(trade.type)}</TableCell>
                          <TableCell className="text-sm text-muted-foreground whitespace-nowrap">{trade.size}</TableCell>
                          {(dataSource === 'capitoltrades' || dataSource === 'combined') && (
                            <TableCell className="text-sm font-medium text-foreground whitespace-nowrap">{trade.price || '—'}</TableCell>
                          )}
                          <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{trade.traded}</TableCell>
                          <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{trade.published}</TableCell>
                          {(dataSource === 'capitoltrades' || dataSource === 'combined') && (
                            <TableCell className="text-xs text-muted-foreground">{trade.owner || '—'}</TableCell>
                          )}
                          {dataSource === 'quiver' && (
                            <TableCell className={`text-xs font-medium whitespace-nowrap ${
                              qt.excessReturn?.startsWith('+') ? 'text-emerald-600 dark:text-emerald-400' :
                              qt.excessReturn?.startsWith('-') ? 'text-rose-600 dark:text-rose-400' :
                              'text-muted-foreground'
                            }`}>
                              {qt.excessReturn || 'N/A'}
                            </TableCell>
                          )}
                          {dataSource === 'quiver' && (
                            <TableCell className="text-xs text-muted-foreground max-w-[150px] truncate" title={qt.description}>
                              {qt.description || '-'}
                            </TableCell>
                          )}
                          <TableCell>
                            {trade.tradeUrl && (
                              <a href={trade.tradeUrl} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                                <ExternalLink className="h-3.5 w-3.5" />
                              </a>
                            )}
                          </TableCell>
                        </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
                {totalPages > 1 && (
                  <div className="flex items-center justify-between px-4 py-3 border-t border-border">
                    <p className="text-xs text-muted-foreground">
                      Showing {(currentPage - 1) * pageSize + 1}–{Math.min(currentPage * pageSize, filteredAndSorted.length)} of {filteredAndSorted.length} trades
                    </p>
                    <div className="flex items-center gap-1">
                      <Button variant="outline" size="sm" className="h-7 text-xs" disabled={currentPage === 1} onClick={() => setCurrentPage(1)}>First</Button>
                      <Button variant="outline" size="sm" className="h-7 text-xs" disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}>Prev</Button>
                      {Array.from({ length: totalPages }, (_, i) => i + 1)
                        .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
                        .reduce<(number | 'ellipsis')[]>((acc, p, i, arr) => {
                          if (i > 0 && p - (arr[i - 1]) > 1) acc.push('ellipsis');
                          acc.push(p);
                          return acc;
                        }, [])
                        .map((item, i) =>
                          item === 'ellipsis' ? (
                            <span key={`e${i}`} className="px-1 text-xs text-muted-foreground">…</span>
                          ) : (
                            <Button
                              key={item}
                              variant={currentPage === item ? 'default' : 'outline'}
                              size="sm"
                              className="h-7 w-7 text-xs p-0"
                              onClick={() => setCurrentPage(item as number)}
                            >
                              {item}
                            </Button>
                          )
                        )}
                      <Button variant="outline" size="sm" className="h-7 text-xs" disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => p + 1)}>Next</Button>
                      <Button variant="outline" size="sm" className="h-7 text-xs" disabled={currentPage === totalPages} onClick={() => setCurrentPage(totalPages)}>Last</Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* History Panel */}
        {showHistory && (
          <Card className="border-muted">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <History className="h-4 w-4 text-muted-foreground" />
                Saved Snapshots
              </CardTitle>
              <CardDescription>
                Each fetch is automatically saved. Click to view historical data.
              </CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              {snapshots.length === 0 ? (
                <div className="p-8 text-center text-muted-foreground">
                  <Clock className="h-8 w-8 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">No snapshots yet. Fetched trades are auto-saved.</p>
                </div>
              ) : (
                <div className="divide-y">
                  {snapshots.map(snap => (
                    <div
                      key={snap.id}
                      className={`flex items-center justify-between px-4 py-3 hover:bg-muted/50 transition-colors ${
                        viewingSnapshot === snap.id ? 'bg-primary/5 border-l-2 border-primary' : ''
                      }`}
                    >
                      <button
                        onClick={() => loadSnapshotTrades(snap.id)}
                        className="flex-1 text-left"
                      >
                        <div className="flex items-center gap-3">
                          <div>
                            <p className="text-sm font-medium text-foreground">
                              {new Date(snap.fetched_at).toLocaleDateString(undefined, {
                                weekday: 'short', year: 'numeric', month: 'short', day: 'numeric'
                              })}
                              <span className="text-muted-foreground font-normal ml-2">
                                {new Date(snap.fetched_at).toLocaleTimeString()}
                              </span>
                            </p>
                            <p className="text-xs text-muted-foreground mt-0.5">
                              {snap.trades_count} trades captured
                            </p>
                          </div>
                        </div>
                      </button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-muted-foreground hover:text-destructive"
                        onClick={(e) => { e.stopPropagation(); deleteSnapshot(snap.id); }}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Disclaimer */}
        <p className="text-xs text-muted-foreground text-center max-w-2xl mx-auto">
          Data sourced from{' '}
          <a href="https://www.capitoltrades.com" target="_blank" rel="noopener noreferrer" className="underline">
            capitoltrades.com
          </a>
          {' '}and{' '}
          <a href="https://www.quiverquant.com/congresstrading/" target="_blank" rel="noopener noreferrer" className="underline">
            quiverquant.com
          </a>
          . Trades are self-reported by members of Congress under the STOCK Act. This information is for educational purposes only and does not constitute financial advice.
        </p>
      </div>

      {/* Politician Scorecard Dialog */}
      <PoliticianScorecard
        open={!!selectedPolitician}
        onOpenChange={(open) => { if (!open) setSelectedPolitician(null); }}
        politicianName={selectedPolitician || ''}
        allTrades={trades}
        onFilterByPolitician={(name) => {
          setPoliticianSearch(name);
          setSelectedPolitician(null);
        }}
      />

      {/* Ticker Scorecard Dialog */}
      <TickerScorecard
        open={!!selectedTicker}
        onOpenChange={(open) => { if (!open) setSelectedTicker(null); }}
        ticker={selectedTicker?.ticker || ''}
        issuer={selectedTicker?.issuer || ''}
        trades={trades
          .filter(t => {
            const tk = (t.ticker && t.ticker.trim()) ? t.ticker.trim() : '';
            return tk === selectedTicker?.ticker;
          })
          .map(t => ({
            politician: t.politician,
            party: t.party,
            type: t.type,
            traded: t.traded,
            size: t.size,
            price: t.price,
          }))
        }
      />
    </div>
  );
};

export default CapitolTrades;
